import { DEAL_STATUS_CONFIG } from '../constants';
import dayjs from 'dayjs';
import React from 'react';
import { FaEye } from 'react-icons/fa';
import { getTeamNameById } from './getTeamInfo';
import { getPstTimeDifference } from '../lib/pstTime';

/**
 * Formats a date/time for "until" display with today/tomorrow logic
 * Assumes the input dateString is already in PST timezone
 * @param dateString - The date string to format (already in PST)
 * @returns Formatted string: "at <time>" for today, "tomorrow at <time>" for tomorrow, or full date/time otherwise
 */
function formatUntilTime(dateString: string): string {
  const now = dayjs(); // Current time in PST (since tz.setDefault is set to America/Los_Angeles)
  const targetDate = dayjs(dateString); // Parse as PST (no timezone conversion needed)
  const tomorrow = now.add(1, 'day');

  if (targetDate.isSame(now, 'day')) {
    // Today - just show time without "at"
    return `${targetDate.format('h:mm A')} PST`;
  } else if (targetDate.isSame(tomorrow, 'day')) {
    // Tomorrow - show "tomorrow at <time>"
    return `tomorrow at ${targetDate.format('h:mm A')} PST`;
  } else {
    // Other days - show full date and time
    return `${targetDate.format('MMM D')} at ${targetDate.format('h:mm A')} PST`;
  }
}

/**
 * Player interface matching the one used in RosterContent
 */
interface Player {
  id: number;
  rwPlayer: {
    id: number;
    first_name: string;
    last_name: string;
    position_1: string;
    position_2: string;
    position_3: string;
    mlb_team: string;
    image?: string;
    bats?: string;
    throws?: string;
    rule_five_eligibility?: string;
    mls?: number;
    options?: string;
    prior_or?: string;
  };
  level: string;
  status: string;
  status_until: string | null;
  playerContract: {
    current_salary?: number;
    total_value?: number | string;
    year_signed?: number;
    salaries?: Array<{ salary: number | string }>;
    contractYears?: Array<{ year: number; salary?: number; type?: string }>;
    contract_types?: Array<{ year: number; type: string }>;
    buyouts?: Array<{ year: number; amount: number }>;
  };
  is_40_man: boolean;
  year: number;
  deal_status?: string | null;
  deal_until?: string | null;
  team_id?: number;
  dealData?: {
    dealId: number | null;
    claims: number;
    bids: number;
    type?: string;
    expires?: string;
    teams?: number[];
  } | null;
}

/**
 * Extracts all available notes for a player and returns them as an array of React elements or strings
 * @param player - The player object to extract notes from
 * @param onDealClick - Optional callback function to handle deal clicks
 * @param myTeam - Optional current user's team ID for claim status checking
 * @returns Array of note elements/strings, empty array if no notes
 */
export const showPlayerNotes = (player: Player, onDealClick?: (dealId: string) => void, myTeam?: number | null, myLeague?: any): (string | React.ReactElement)[] => {
  const notes: (string | React.ReactElement)[] = [];

  // Franchise tag display - should be first

  const isFranchiseTag = player.playerContract?.contractYears?.[0]?.contract_type === "Franchise Tag";
  if (isFranchiseTag) {
    const year = player.playerContract?.contractYears?.[0]?.year || '';
    notes.push(
      React.createElement('span', {
        key: 'franchise-tag',
        style: {
          backgroundColor: '#15803d',
          color: 'white',
          padding: '2px 8px',
          borderRadius: '4px',
          fontSize: '12px',
          fontWeight: 'bold',
          display: 'inline-block',
          marginRight: '4px'
        }
      }, `${year} Franchise Tag`)
    );
    return notes; // Return early so no other notes appear after franchise tag
  }


  // Extendec contract display - should be second

  const isExtendedContract = player.playerContract?.contractYears?.[0]?.contract_type === "Contract Extension";
  if (isExtendedContract) {
    const year = player.playerContract?.contractYears?.[0]?.year || '';
    notes.push(
      React.createElement('span', {
        key: 'extended-contract',
        style: {
          backgroundColor: '#15803d',
          color: 'white',
          padding: '2px 8px',
          borderRadius: '4px',
          fontSize: '12px',
          fontWeight: 'bold',
          display: 'inline-block',
          marginRight: '4px'
        }
      }, `Extended Contract`)
    );
    return notes; // Return early so no other notes appear after franchise tag
  }

  // Deal status notes - combine status and until time into one line
  // Skip deal_status display if player is in "Awaiting Assignment" status (it's obvious from the section)
  if (player.deal_status && player.status !== 'Awaiting Assignment') {
    if (player.deal_status === 'settlement_free_agent_offer') {
      // Special handling for settlement_free_agent_offer
      if (player.dealData?.teams && player.dealData.teams.length > 0) {
        const signingTeam = myLeague ? getTeamNameById(player.dealData.teams[0], myLeague) : `Team ${player.dealData.teams[0]}`;
        notes.push(
          React.createElement('div', {
            key: 'settlement',
            style: { marginBottom: '4px' }
          }, [
            React.createElement('div', {
              key: 'signed-by',
              style: { color: '#48bb78', fontWeight: 'bold', marginBottom: '2px' }
            }, `Signed by ${signingTeam}`),
            React.createElement('div', {
              key: 'settlement-closes',
              style: { color: '#48bb78' }
            }, 'Settlement closes at midnight')
          ])
        );
      } else {
        notes.push('Signed by Unknown Team');
        notes.push('Settlement closes at midnight');
      }
    } else if (player.deal_status === 'settlement_waiver_claim') {
      // Special handling for settlement_waiver_claim
      if (player.dealData?.teams && player.dealData.teams.length > 0) {
        const claimingTeam = myLeague ? getTeamNameById(player.dealData.teams[0], myLeague) : `Team ${player.dealData.teams[0]}`;
        notes.push(
          React.createElement('div', {
            key: 'settlement',
            style: { marginBottom: '4px' }
          }, [
            React.createElement('div', {
              key: 'claimed-by',
              style: { color: '#48bb78', fontWeight: 'bold', marginBottom: '2px' }
            }, `Claimed by ${claimingTeam}`),
            React.createElement('div', {
              key: 'settlement-closes',
              style: { color: '#48bb78' }
            }, 'Settlement closes at midnight')
          ])
        );
      } else {
        notes.push('Claimed by Unknown Team');
        notes.push('Settlement closes at midnight');
      }
    } else if (player.deal_status === 'free_agent_offer') {
      // Special handling for free_agent_offer - show pending offers
      if (player.dealData?.bids && player.dealData.bids > 0) {
        const offerText = player.dealData.bids === 1 ? '1 offer' : `${player.dealData.bids} offers`;
        const checkMarks = '✓'.repeat(player.dealData.bids);
        notes.push(
          React.createElement('div', {
            key: 'offers',
            style: { marginBottom: '4px' }
          }, `${offerText} made  `,
            React.createElement('span', {
              key: 'checkmarks',
              style: { color: '#48bb78' }
            }, checkMarks))
        );

        if (player.dealData.expires) {
          notes.push(
            React.createElement('div', {
              key: 'expires',
              style: { fontStyle: 'italic', fontSize: '0.9em' }
            }, `Accepting offers until ${formatUntilTime(player.dealData.expires)}`)
          );
        }
      }
    } else if (player.deal_status === 'option_hold') {
      // Special handling for option_hold
      notes.push('Optioned to AAA');
      if (player.deal_until) {
        const untilTime = formatUntilTime(player.deal_until);
        notes.push(`Available for recall ${untilTime}`);
      } else {
        notes.push('Available for recall');
      }
    } else if (player.deal_status === 'waivers_outright') {
      // Special handling for waivers_outright
      notes.push('On waivers');
      if (player.deal_until) {
        const untilTime = formatUntilTime(player.deal_until);
        notes.push(`Waivers expire ${untilTime}`);
      } else {
        notes.push('Waivers expire');
      }
    } else if (player.deal_status === 'qualifying_offer_made') {
      // Special handling for qualifying_offer_made
      notes.push('Qualifying offer made');
      if (player.deal_until) {
        const untilTime = formatUntilTime(player.deal_until);
        notes.push(`Decision by ${untilTime}`);
      } else {
        notes.push('Decision pending');
      }
    } else {
      // Skip free agent related statuses since they're redundant with deal_status display
      // But allow settlement_free_agent_offer (already handled above) and free_agent_offer
      if (player.deal_status.toLowerCase().includes('free') && player.deal_status !== 'settlement_free_agent_offer' && player.deal_status !== 'free_agent_offer') {
        // Don't add any notes for free agent statuses
        return notes;
      }

      // Skip "awaiting_assignment" deal_status since it's obvious from the section
      if (player.deal_status.toLowerCase().includes('awaiting_assignment')) {
        // Don't show deal_status for awaiting assignment - it's obvious from the section
        return notes;
      }

      const statusConfig = DEAL_STATUS_CONFIG[player.deal_status as keyof typeof DEAL_STATUS_CONFIG];
      let statusText: string;
      if (statusConfig) {
        statusText = statusConfig.text;
      } else {
        // Convert unrecognized status to Sentence case
        // Replace underscores with spaces, then capitalize only the first letter
        const words = player.deal_status.replace(/_/g, ' ').split(' ');
        statusText = words[0].charAt(0).toUpperCase() + words[0].slice(1).toLowerCase() +
          (words.length > 1 ? ' ' + words.slice(1).join(' ').toLowerCase() : '');
      }

      // Check if this is a settlement or review status that should be clickable
      const isSettlementOrReview = player.deal_status.toLowerCase().includes('settlement') ||
        player.deal_status.toLowerCase().includes('review');
      const isSettlementTrade = player.deal_status === 'settlement_trade';

      if (player.deal_until) {
        const isWaiverStatus = player.deal_status === 'waivers' || player.deal_status === 'waived' || player.deal_status === 'claimed';
        const untilTime = formatUntilTime(player.deal_until);

        if (isSettlementOrReview) {
          // Create clickable link with view icon
          notes.push(
            React.createElement('span', { key: 'deal-status' },
              React.createElement('a', {
                href: player.dealData?.dealId ? `/deals/${player.dealData.dealId}` : '#',
                style: { color: '#48bb78', textDecoration: 'underline', cursor: 'pointer' },
                onClick: (e: React.MouseEvent) => {
                  e.preventDefault();
                  e.stopPropagation();
                  // Open deal modal
                  if (player.dealData?.dealId) {
                    if (onDealClick) {
                      onDealClick(player.dealData.dealId.toString());
                    } else {
                      console.log(`Navigate to deal ${player.dealData.dealId}`);
                    }
                  } else {
                    alert('Deal not available');
                  }
                }
              }, statusText),
              ' ',
              isSettlementTrade
                ? 'at midnight'
                : isWaiverStatus
                  ? 'claims through'
                  : 'until',
              !isSettlementTrade ? [' ', untilTime] : null,
              ' ',
              React.createElement(FaEye, {
                style: { color: '#48bb78', cursor: 'pointer', marginLeft: '4px', display: 'inline', fontSize: '16px', verticalAlign: 'text-top' },
                onClick: (e: React.MouseEvent) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (player.dealData?.dealId) {
                    console.log(`Navigate to deal ${player.dealData.dealId}`);
                    if (onDealClick) {
                      onDealClick(player.dealData.dealId.toString());
                    }
                  } else {
                    alert('Deal not available');
                  }
                }
              })
            )
          );
        } else {
          // Skip option_hold here too - it will be handled in the status section
          if (player.deal_status !== 'option_hold') {
            if (isSettlementTrade) {
              notes.push('Settling trade at midnight');
            } else {
              notes.push(`${isWaiverStatus ? 'Waivers through' : `${statusText} until`} ${untilTime}`);
            }
          }
        }

        // Add claims bullet immediately after deal status for waived/claimed players (but not if in settlement)
        if ((player.deal_status.toLowerCase().includes('waiv') || player.deal_status.toLowerCase().includes('claim')) &&
          player.deal_status !== 'settlement_waiver_claim' &&
          player.dealData &&
          player.dealData.claims > 0) {
          const claimsText = player.dealData.claims === 1 ? '1 claim' : `${player.dealData.claims} claims`;
          const checkMarks = '✓'.repeat(player.dealData.claims);
          notes.push(
            React.createElement('span', { key: 'claims' },
              claimsText,
              ' ',
              React.createElement('span', {
                style: { color: '#48bb78', fontSize: '14px' }
              }, checkMarks)
            )
          );

          // Add "Your team put in a claim" note if deal_status is "claimed" and my team is in the teams array
          console.log('CLAIM MESSAGE DEBUG:');
          console.log('deal_status:', player.deal_status);
          console.log('myTeam:', myTeam);
          console.log('dealData.teams:', player.dealData?.teams);
          console.log('teams some check:', player.dealData?.teams?.some((teamId: number) => Number(teamId) === Number(myTeam)));

          if (player.deal_status === 'claimed' &&
            myTeam &&
            player.dealData.teams &&
            player.dealData.teams.some((teamId: number) => Number(teamId) === Number(myTeam))) {
            notes.push(
              React.createElement('span', {
                key: 'my-team-claim',
                style: { color: '#48bb78', fontWeight: 'bold' }
              }, 'Your team put in a claim')
            );
          }
        }
      } else {
        if (isSettlementOrReview) {
          // Create clickable link without time
          notes.push(
            React.createElement('span', { key: 'deal-status' },
              React.createElement('a', {
                href: player.dealData?.dealId ? `/deals/${player.dealData.dealId}` : '#',
                style: { color: '#48bb78', textDecoration: 'underline', cursor: 'pointer' },
                onClick: (e: React.MouseEvent) => {
                  e.preventDefault();
                  if (player.dealData?.dealId) {
                    console.log(`Navigate to deal ${player.dealData.dealId}`);
                    if (onDealClick) {
                      onDealClick(player.dealData.dealId.toString());
                    }
                  } else {
                    alert('Deal not available');
                  }
                }
              }, statusText),
              ' ',
              React.createElement(FaEye, {
                style: { color: '#48bb78', cursor: 'pointer', marginLeft: '4px', display: 'inline', fontSize: '16px', verticalAlign: 'text-top' },
                onClick: (e: React.MouseEvent) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (player.dealData?.dealId) {
                    console.log(`Navigate to deal ${player.dealData.dealId}`);
                    if (onDealClick) {
                      onDealClick(player.dealData.dealId.toString());
                    }
                  } else {
                    alert('Deal not available');
                  }
                }
              })
            )
          );
        } else {
          // Skip option_hold here too - it will be handled in the status section
          if (player.deal_status !== 'option_hold') {
            notes.push(statusText);
          }
        }

        // Add claims bullet immediately after deal status for waived/claimed players (but not if in settlement)
        if ((player.deal_status.toLowerCase().includes('waiv') || player.deal_status.toLowerCase().includes('claim')) &&
          player.deal_status !== 'settlement_waiver_claim' &&
          player.dealData &&
          player.dealData.claims > 0) {
          const claimsText = player.dealData.claims === 1 ? '1 claim' : `${player.dealData.claims} claims`;
          const checkMarks = '✓'.repeat(player.dealData.claims);
          notes.push(
            React.createElement('span', { key: 'claims' },
              claimsText,
              ' ',
              React.createElement('span', {
                style: { color: '#48bb78', fontSize: '14px' }
              }, checkMarks)
            )
          );

          // Add "Your team put in a claim" note if deal_status is "claimed" and my team is in the teams array
          if (player.deal_status === 'claimed' &&
            myTeam &&
            player.dealData.teams &&
            player.dealData.teams.some((teamId: number) => Number(teamId) === Number(myTeam))) {
            notes.push(
              React.createElement('span', {
                key: 'my-team-claim-2',
                style: { color: '#48bb78', fontWeight: 'bold' }
              }, 'Your team put in a claim')
            );
          }
        }
      }
    }
  }

  // Status until date - combine status and until on same line
  if (player.status) {
    // For Active players on the active roster, suppress status-based notes entirely.
    // Deal status notes above will still be shown.
    if (player.status === 'Active') {
      // Do not add any status/status_until note when status is Active
    } else if (player.status === 'Option Hold') {
      // Special handling for Option Hold status (only when status is 'Option Hold', not deal_status)
      notes.push('Optioned to AAA');
      if (player.status_until != null && getPstTimeDifference(player.status_until) > 0) {
        const untilTime = formatUntilTime(player.status_until);
        notes.push(`Available for recall ${untilTime}`);
      } else {
        notes.push('Available for recall');
      }
    } else if (
      player.status === 'DL10' ||
      player.status?.toLowerCase() === 'dl10' ||
      player.status === '10 day dl' ||
      player.status === '10-day dl' ||
      player.status === '10 day il' ||
      player.status === '10-day il'
    ) {
      // 10-day IL: section already shows "10 Day Injured List", so notes just say available for recall
      // Only show "until <time>" when status_until is in the future; once passed or null, just "Available for recall"
      if (player.status_until != null && getPstTimeDifference(player.status_until) > 0) {
        const untilTime = formatUntilTime(player.status_until);
        notes.push(`Available for recall ${untilTime}`);
      } else {
        notes.push('Available for recall');
      }
    } else {
      const statusText = player.status.replace('DL', 'Injured List-').replace('dfa', 'DFA');
      if (player.status_until) {
        const date = new Date(player.status_until);
        notes.push(`${statusText} until ${date.toLocaleDateString()}`);
      } else if (player.status !== 'Active' && player.status !== 'Inactive') {
        notes.push(statusText);
      }
    }
  } else if (player.status_until) {
    // Only show until date if there's no status but there is a status_until
    const date = new Date(player.status_until);
    notes.push(`Until ${date.toLocaleDateString()}`);
  }


  return notes;
};

/**
 * Alternative function that returns notes as a single formatted string
 * @param player - The player object to extract notes from
 * @param separator - String to separate multiple notes (default: ', ')
 * @returns Formatted string of notes, empty string if no notes
 */
export const showPlayerNotesAsString = (player: Player, separator: string = ', '): string => {
  const notes = showPlayerNotes(player);
  return notes.join(separator);
}; 